import { Box, Typography } from '@mui/material';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export type UserStatusDatum = {
  label: string;
  dayLabel: string;
  activeUsers: number;
  participationCompletedUsers: number;
};

const ACTIVE_USERS_COLOR = '#E57373';
const PARTICIPATION_COLOR = '#64B5F6';
const ACTIVE_GRADIENT_ID = 'colorActiveUsers';
const PARTICIPATION_GRADIENT_ID = 'colorParticipation';

type Props = {
  data?: UserStatusDatum[];
};

export default function RouletteContentUserStatusChart({ data }: Props) {
  const chartData = data ?? [];

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 3,
        p: 3,
        mt: 3,
        border: '1px solid #f1f5f9',
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={2}
        textAlign="center"
        color="#475569"
      >
        사용자 현황
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 48, left: 8, bottom: 8 }}>
          <defs>
            <linearGradient id={ACTIVE_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={ACTIVE_USERS_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={ACTIVE_USERS_COLOR} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={PARTICIPATION_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={PARTICIPATION_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={PARTICIPATION_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
          <XAxis dataKey="dayLabel" tick={{ fontSize: 12 }} interval={0} />
          <YAxis
            tickFormatter={(v) => (v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR'))}
            tick={{ fontSize: 11 }}
            domain={([_dataMin, dataMax]: [number, number]) => {
              if (dataMax <= 0) return [0, 10];
              const target = dataMax * 1.1;
              // 데이터 규모에 비례한 눈금 단위로 반올림 (예: 수천 단위는 500 단위, 수백만 단위는 50만 단위)
              const magnitude = 10 ** Math.floor(Math.log10(target));
              const step = magnitude / 2;
              return [0, Math.ceil(target / step) * step];
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload as UserStatusDatum;
              if (!row) return null;
              return (
                <Box
                  sx={{
                    p: 1.5,
                    minWidth: 180,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {row.dayLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    활성 사용자 수: {row.activeUsers.toLocaleString('ko-KR')}명
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0 }}>
                    참여 완료 사용자 수: {row.participationCompletedUsers.toLocaleString('ko-KR')}명
                  </Typography>
                </Box>
              );
            }}
          />
          <Legend wrapperStyle={{ paddingTop: 8 }} />
          <Area
            type="monotone"
            dataKey="activeUsers"
            stroke={ACTIVE_USERS_COLOR}
            strokeWidth={2}
            fill={`url(#${ACTIVE_GRADIENT_ID})`}
            fillOpacity={1}
            name="활성 사용자 수"
          />
          <Area
            type="monotone"
            dataKey="participationCompletedUsers"
            stroke={PARTICIPATION_COLOR}
            strokeWidth={2}
            fill={`url(#${PARTICIPATION_GRADIENT_ID})`}
            fillOpacity={1}
            name="참여 완료 사용자 수"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
